import { useEffect } from "react";
import { useDaily } from "@daily-co/daily-react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useLocation, useHistory } from "react-router";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { parseEventCode } from "modules/queryParams/queryParamsHelper";
import logger from "logging/logger";
import {
  GEARTEST_ROOM_JOIN,
  PUBLIC_GEARTEST_PREFIX,
} from "modules/systemCheck/constants";
import { useDailyToken } from "modules/audioVideo/hooks/useDailyToken/useDailyToken";
import { getDailyMeetingUrl } from "modules/audioVideo/hooks/useJoinMeeting/urls";
import { UserNotRegisteredTokenError } from "modules/audioVideo/hooks/useDailyToken/constants";
import createRoomFromEventCode from "./request";

const RETRY_COUNT = 2;

export const useGeartestDailyRoomJoin = () => {
  const daily = useDaily();
  const { getToken } = useDailyToken();
  const location = useLocation();
  const history = useHistory();
  const params = useQueryParams();
  const eventCode = parseEventCode(params);

  const isCheckEnabled =
    Boolean(daily) && Boolean(eventCode || location.pathname === "/geartest");

  const {
    data: participantData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [GEARTEST_ROOM_JOIN],

    queryFn: async () => {
      if (!daily) {
        throw new Error("no daily call object");
      }

      let meetingId: string;

      if (eventCode) {
        const { eventId } = await createRoomFromEventCode(eventCode);
        meetingId = eventId;
      } else {
        meetingId = `${PUBLIC_GEARTEST_PREFIX}${moment
          .utc()
          .format("YYYYMMDD")}`;
      }

      const token = await getToken({
        meetingId: eventCode ? meetingId : "geartest",
        isCameraOn: true,
        isMicrophoneOn: true,
        ignoreCache: !eventCode, // no eventcode = public geartest, ignore cache
      });
      const meetingState = daily.meetingState();

      if (meetingState === "joined-meeting") {
        await daily.leave();
      }

      return daily.join({
        token,
        url: getDailyMeetingUrl({ meetingId }),
        subscribeToTracksAutomatically: false,
      });
    },

    // only turn on in-event or for public geartest
    enabled: isCheckEnabled,
    retry: (failureCount, error) => {
      const noOfRetries =
        error instanceof UserNotRegisteredTokenError ? 0 : RETRY_COUNT;

      return failureCount !== noOfRetries;
    },
  });

  useEffect(() => {
    if (!error) return;

    if (error instanceof UserNotRegisteredTokenError) {
      if (eventCode) {
        // could show a notification here later
        history.push(`/e/${eventCode}/register`);
      } else {
        logger.error(
          "[useGeartestDailyRoomJoin] token is null while joining a room on public geartest",
        );
      }
      return;
    }

    const message = error.message || "unknown";
    logger.error(`[geartest][Daily][useGeartestDailyRoomJoin] ${message}`, {
      error,
    });
  }, [error, eventCode, history]);

  return {
    participantData,
    isSuccessful: isCheckEnabled ? !!participantData : null,
    isLoading,
  };
};
